import { useState } from 'react';
import { PICKABLE_TYPES, fieldTypeLabel } from '~/components/workbench/fields';
import type { FieldType } from '~/components/workbench/fields/sdk';
import { apiDefineMetaobject, apiDeleteDataDefinition, type DataDefinition } from '~/lib/api/data';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  ErrorBanner,
  Select,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import {
  MAX_FIELDS,
  apiErrorMessage,
  draftFields,
  emptyTypeDraft,
  moveItem,
  newFieldDraft,
  toKey,
  typeDraftFrom,
  typeDraftProblem,
  type FieldDraft,
  type TypeDraft,
} from './content-model';

/** Create or edit a content type (`custom` namespace only). */
export function TypeEditor({
  appId,
  existing,
  types,
  onCancel,
  onSaved,
  onDeleted,
}: {
  appId: string;
  existing?: DataDefinition;
  types: readonly DataDefinition[];
  onCancel: () => void;
  onSaved: (saved: DataDefinition) => void;
  onDeleted: (id: string) => void;
}) {
  const [draft, setDraft] = useState<TypeDraft>(() => (existing ? typeDraftFrom(existing) : emptyTypeDraft()));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'delete' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tried, setTried] = useState(false);
  const problem = typeDraftProblem(draft);
  const targets = types.filter((t) => t.namespace === 'custom' && t.key !== draft.type);

  const patch = (next: Partial<TypeDraft>) => setDraft((d) => ({ ...d, ...next }));
  const patchField = (uid: string, next: Partial<FieldDraft>) =>
    setDraft((d) => ({ ...d, fields: d.fields.map((f) => (f.uid === uid ? { ...f, ...next } : f)) }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);

    if (problem) {
      return;
    }

    setBusy('save');
    setError(null);

    try {
      const saved = await apiDefineMetaobject(appId, {
        type: draft.type,
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        fields: draftFields(draft),
        displayField: draft.displayField || undefined,
        access: { storefront: draft.isPublic ? 'public' : 'none' },
      });

      onSaved({ ...saved, entryCount: saved.entryCount ?? existing?.entryCount ?? 0 });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the content type.'));
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!existing) {
      return;
    }

    setBusy('delete');
    setError(null);

    try {
      await apiDeleteDataDefinition(appId, existing.id);
      onDeleted(existing.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the content type.'));
      setBusy(null);
    }
  };

  return (
    <form className="flex-1 space-y-5 overflow-y-auto p-4" onSubmit={(e) => void save(e)}>
      <button type="button" className={classNames(BTN_GHOST, '-ml-2.5')} onClick={onCancel}>
        <div className="i-ph:arrow-left text-[12px]" />
        Back
      </button>

      <h4 className="text-[15px] font-medium tracking-tight text-primary">
        {existing ? `Edit ${existing.name}` : 'New content type'}
      </h4>

      <div className={classNames(CARD, 'space-y-4 p-4')}>
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">Name (singular)</span>
          <input
            className="ui-input"
            value={draft.name}
            placeholder="Team member"
            onChange={(e) =>
              patch({ name: e.target.value, ...(draft.typeTouched ? {} : { type: toKey(e.target.value) }) })
            }
          />
        </label>
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">Handle</span>
          <input
            className="ui-input-mono"
            value={draft.type}
            placeholder="team_member"
            disabled={Boolean(existing)}
            onChange={(e) => patch({ type: e.target.value, typeTouched: true })}
          />
          <span className="block text-[11px] leading-relaxed text-muted">
            The site reads it with useMetaobjects('{draft.type || 'team_member'}'). It can't change later.
          </span>
        </label>
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">Description (optional)</span>
          <input
            className="ui-input"
            value={draft.description}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </label>
        <Switch
          checked={draft.isPublic}
          onChange={(isPublic) => patch({ isPublic })}
          label="Your site can show this content"
        />
      </div>

      <section className="space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <h4 className={CAPTION}>Fields</h4>
          <span className="text-[11px] text-muted">
            {draft.fields.length}/{MAX_FIELDS}
          </span>
        </div>
        {draft.fields.map((f, i) => (
          <FieldDraftCard
            key={f.uid}
            field={f}
            index={i}
            count={draft.fields.length}
            targets={targets}
            isTitle={draft.displayField === f.key && Boolean(f.key)}
            onChange={(next) => patchField(f.uid, next)}
            onTitle={() => patch({ displayField: f.key })}
            onMove={(to) => setDraft((d) => ({ ...d, fields: moveItem(d.fields, i, to) }))}
            onRemove={() =>
              setDraft((d) => ({
                ...d,
                fields: d.fields.filter((x) => x.uid !== f.uid),
                displayField: d.displayField === f.key ? '' : d.displayField,
              }))
            }
          />
        ))}
        {draft.fields.length < MAX_FIELDS && (
          <button
            type="button"
            className={BTN_SECONDARY}
            onClick={() => setDraft((d) => ({ ...d, fields: [...d.fields, newFieldDraft()] }))}
          >
            <div className="i-ph:plus text-[13px]" />
            Add field
          </button>
        )}
        {existing && (
          <p className="text-[11px] leading-relaxed text-muted">
            Removing a field hides its values; changing a field's type applies to new edits.
          </p>
        )}
      </section>

      {tried && problem && <ErrorBanner message={problem} />}
      {error && <ErrorBanner message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {existing ? (
          confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className={classNames(BTN_SECONDARY, '!text-danger')}
                disabled={busy !== null}
                onClick={() => void remove()}
              >
                {busy === 'delete' ? <Spinner /> : <div className="i-ph:trash text-[13px]" />}
                Delete type and {existing.entryCount ?? 0} entries
              </button>
              <button type="button" className={BTN_GHOST} onClick={() => setConfirmDelete(false)}>
                Keep
              </button>
            </div>
          ) : (
            <button type="button" className={BTN_GHOST} onClick={() => setConfirmDelete(true)}>
              <div className="i-ph:trash text-[13px]" />
              Delete type
            </button>
          )
        ) : (
          <span />
        )}
        <div className="flex items-center gap-1.5">
          <button type="button" className={BTN_GHOST} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="ui-btn-accent !px-4 !py-2 !text-[12px]" disabled={busy !== null}>
            {busy === 'save' && <Spinner />}
            {existing ? 'Save type' : 'Create type'}
          </button>
        </div>
      </div>
    </form>
  );
}

function FieldDraftCard({
  field,
  index,
  count,
  targets,
  isTitle,
  onChange,
  onTitle,
  onMove,
  onRemove,
}: {
  field: FieldDraft;
  index: number;
  count: number;
  targets: readonly DataDefinition[];
  isTitle: boolean;
  onChange: (next: Partial<FieldDraft>) => void;
  onTitle: () => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className={classNames(CARD, 'space-y-3 p-3.5')}>
      <div className="flex items-start gap-2">
        <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
          <input
            aria-label={`Field ${index + 1} label`}
            className="ui-input"
            placeholder="Label, e.g. Photo"
            value={field.label}
            onChange={(e) =>
              onChange({ label: e.target.value, ...(field.keyTouched ? {} : { key: toKey(e.target.value) }) })
            }
          />
          <Select
            label={`Field ${index + 1} type`}
            value={field.type}
            onChange={(type) => onChange({ type: type as FieldType })}
          >
            {PICKABLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {fieldTypeLabel(t)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            className="ui-btn-icon"
            aria-label="Move field up"
            disabled={index === 0}
            onClick={() => onMove(index - 1)}
          >
            <span className="i-ph:caret-up text-[12px] text-muted" />
          </button>
          <button
            type="button"
            className="ui-btn-icon"
            aria-label="Move field down"
            disabled={index === count - 1}
            onClick={() => onMove(index + 1)}
          >
            <span className="i-ph:caret-down text-[12px] text-muted" />
          </button>
          <button
            type="button"
            className="ui-btn-icon"
            aria-label="Remove field"
            disabled={count === 1}
            onClick={onRemove}
          >
            <span className="i-ph:x text-[12px] text-muted" />
          </button>
        </div>
      </div>

      {field.type === 'select' && (
        <textarea
          aria-label="Options"
          rows={2}
          className="ui-input resize-y"
          placeholder="One option per line: Small, Medium, Large"
          value={field.optionsText}
          onChange={(e) => onChange({ optionsText: e.target.value })}
        />
      )}

      {field.type === 'metaobject_reference' && (
        <Select label="Points to" value={field.targetType} onChange={(targetType) => onChange({ targetType })}>
          <option value="">Points to which content type?</option>
          {targets.map((t) => (
            <option key={t.id} value={t.key}>
              {t.name}
            </option>
          ))}
        </Select>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Switch checked={field.required} onChange={(required) => onChange({ required })} label="Required" />
        <Switch checked={field.list} onChange={(list) => onChange({ list })} label="Several values" />
        {field.type === 'single_line_text' && !field.list && (
          <Switch checked={isTitle} onChange={() => onTitle()} label="Use as title" />
        )}
        <label className="ml-auto flex items-center gap-1.5">
          <span className="text-[11px] text-muted">Key</span>
          <input
            aria-label="Field key"
            className="ui-input-mono !w-36 !py-1"
            value={field.key}
            onChange={(e) => onChange({ key: e.target.value, keyTouched: true })}
          />
        </label>
      </div>
    </div>
  );
}
